import React, { useState, useEffect, useCallback } from 'react';
import { useAppConfig } from './useAppConfig';
import { useTheme, fontFamily, STATUS_OPTIONS, getThStyle, getTdStyle, ThemeButton } from './theme';

const getTabStyle = (theme, active) => ({
  background: active ? theme.buttonBg : theme.cardBg,
  color: active ? theme.buttonText : theme.text,
  border: `1px solid ${active ? theme.buttonBg : theme.borderColor}`,
  borderRadius: 6,
  padding: '8px 20px',
  fontWeight: 600,
  fontSize: 15,
  cursor: 'pointer',
});

const inputStyle = (theme, extra = {}) => ({
  padding: '10px 14px',
  borderRadius: 6,
  border: `1px solid ${theme.borderColor}`,
  fontSize: 16,
  background: theme.inputBg,
  color: theme.text,
  transition: 'background 0.2s, color 0.2s',
  ...extra,
});

const primaryButtonStyle = (theme) => ({
  background: theme.buttonBg,
  color: theme.buttonText,
  border: 'none',
  borderRadius: 6,
  padding: '10px 24px',
  fontWeight: 600,
  fontSize: 16,
  cursor: 'pointer',
  transition: 'background 0.2s',
});

function AdminNoteCell({ theme, request, onSave }) {
  const [value, setValue] = useState(request.adminNote || '');
  const [savedValue, setSavedValue] = useState(request.adminNote || '');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const dirty = value !== savedValue;

  useEffect(() => { setValue(request.adminNote || ''); setSavedValue(request.adminNote || ''); }, [request.id]);

  useEffect(() => {
    if (!justSaved) return;
    const t = setTimeout(() => setJustSaved(false), 1500);
    return () => clearTimeout(t);
  }, [justSaved]);

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try { if (await onSave(request.id, value)) { setSavedValue(value); setJustSaved(true); } }
    finally { setSaving(false); }
  };

  return (
    <div>
      <textarea
        rows={2}
        value={value}
        placeholder="Internal note…"
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        style={inputStyle(theme, { width: 280, minHeight: 46, resize: 'vertical', fontSize: 14, padding: '6px 8px', fontFamily })}
      />
      <div style={{ fontSize: 12, marginTop: 4, minHeight: 16, color: dirty ? theme.subtext : theme.successText }}>
        {saving ? 'Saving…' : dirty
          ? <button onClick={save} style={{ ...primaryButtonStyle(theme), padding: '2px 10px', fontSize: 12 }}>Save</button>
          : justSaved ? 'Saved' : ''}
      </div>
    </div>
  );
}

function Admin() {
  const config = useAppConfig();
  const { theme, darkMode, toggleDarkMode } = useTheme();
  const [adminPassword, setAdminPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState('requests');
  const [error, setError] = useState('');

  // Requests state
  const [requests, setRequests] = useState([]);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // IP names state
  const [ipNames, setIpNames] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [newName, setNewName] = useState('');
  const [ipBusy, setIpBusy] = useState(false);
  const [ipMessage, setIpMessage] = useState('');
  const [ipError, setIpError] = useState('');
  const [ipLastUpdated, setIpLastUpdated] = useState(null);

  const fetchRequests = useCallback(async (password) => {
    setError('');
    try {
      const res = await fetch(`${config.BACKEND_URL}/api/requests`, { headers: { 'x-admin-password': password } });
      if (res.status === 401) {
        setError('Incorrect admin password.');
        setRequests([]);
        return false;
      }
      setRequests(await res.json());
      setLastUpdated(new Date().toLocaleTimeString());
      return true;
    } catch {
      setError('Failed to fetch requests.');
      setRequests([]);
      return false;
    }
  }, [config?.BACKEND_URL]);

  const fetchIpNames = useCallback(async (password) => {
    setError('');
    try {
      const res = await fetch(`${config.BACKEND_URL}/api/ip-names`, { headers: { 'x-admin-password': password } });
      if (res.status === 401) {
        setError('Incorrect admin password.');
        setIpNames([]);
        return false;
      }
      const data = await res.json();
      setIpNames(Array.isArray(data) ? data : []);
      setIpLastUpdated(new Date().toLocaleTimeString());
      return true;
    } catch {
      setError('Failed to fetch IP names.');
      setIpNames([]);
      return false;
    }
  }, [config?.BACKEND_URL]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!adminPassword) { setError('Please enter the admin password.'); return; }
    const ok = await fetchRequests(adminPassword);
    if (ok) {
      setAuthed(true);
      fetchIpNames(adminPassword);
    }
  };

  useEffect(() => {
    if (!authed) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (tab === 'requests') fetchRequests(adminPassword);
      else fetchIpNames(adminPassword);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [authed, adminPassword, tab, fetchRequests, fetchIpNames]);

  const updateStatus = async (id, status) => {
    await fetch(`${config.BACKEND_URL}/api/request/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'x-admin-password': adminPassword },
      body: JSON.stringify({ status }),
    });
    fetchRequests(adminPassword);
  };

  const saveAdminNote = async (id, adminNote) => {
    try {
      const res = await fetch(`${config.BACKEND_URL}/api/request/${id}/admin-note`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-admin-password': adminPassword },
        body: JSON.stringify({ adminNote }),
      });
      if (!res.ok) { setError(`Failed to save admin note (HTTP ${res.status}).`); return false; }
      return true;
    } catch {
      setError('Failed to save admin note.');
      return false;
    }
  };

  const deleteRequest = async (id) => {
    await fetch(`${config.BACKEND_URL}/api/request/${id}`, { method: 'DELETE', headers: { 'x-admin-password': adminPassword } });
    fetchRequests(adminPassword);
  };

  const addIpName = async (e) => {
    e.preventDefault();
    const ip = newIp.trim();
    const name = newName.trim();
    setIpMessage('');
    setIpError('');
    if (!ip || !name) { setIpError('Both IP address and name are required.'); return; }

    setIpBusy(true);
    try {
      const res = await fetch(`${config.BACKEND_URL}/api/ip-names`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-password': adminPassword },
        body: JSON.stringify({ ip, name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setIpError(data.error || `Failed to add mapping (HTTP ${res.status}).`);
        return;
      }
      setNewIp('');
      setNewName('');
      setIpMessage(`Mapped ${ip} → ${name}`);
      await fetchIpNames(adminPassword);
    } catch {
      setIpError('Failed to add mapping.');
    } finally {
      setIpBusy(false);
    }
  };

  const deleteIpName = async (ip, name) => {
    setIpMessage('');
    setIpError('');
    setIpBusy(true);
    try {
      const res = await fetch(`${config.BACKEND_URL}/api/ip-names/${encodeURIComponent(ip)}`, {
        method: 'DELETE',
        headers: { 'x-admin-password': adminPassword },
      });
      if (res.status === 404) {
        setIpError(`Mapping for ${ip} was not found.`);
      } else if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setIpError(data.error || `Failed to delete mapping (HTTP ${res.status}).`);
        return;
      } else {
        setIpMessage(`Deleted ${name} (${ip})`);
      }
      await fetchIpNames(adminPassword);
    } catch {
      setIpError('Failed to delete mapping.');
    } finally {
      setIpBusy(false);
    }
  };

  const filteredRequests = hideCompleted
    ? requests.filter((r) => r.status !== 'Added' && r.status !== "Can't Find" && r.status !== 'Duplicate')
    : requests;

  const sortedIpNames = [...ipNames].sort(
    (a, b) => (a.name || '').localeCompare(b.name || '') || (a.ip || '').localeCompare(b.ip || '')
  );

  return (
    <div style={{ maxWidth: 1560, margin: '2.5rem auto', fontFamily, background: theme.bgColor, minHeight: '100vh', padding: '2rem 2rem 4rem 2rem', borderRadius: 18, boxShadow: theme.shadow, color: theme.text, transition: 'background 0.3s, color 0.3s' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <h1 style={{ color: theme.mainColor, fontWeight: 700, letterSpacing: '-1px', fontSize: 36, marginBottom: 0, flex: 1 }}>Admin</h1>
        <a href="/" style={{ color: theme.mainColor, fontWeight: 600, textDecoration: 'none' }}>← Back to site</a>
        <ThemeButton theme={theme} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      </div>

      {!authed ? (
        <form onSubmit={handleLogin} style={{ marginTop: 24 }}>
          <input
            type="password"
            name="admin_password"
            autoComplete="current-password"
            placeholder="Admin password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            style={inputStyle(theme, { width: 240, marginRight: 10 })}
          />
          <button type="submit" style={primaryButtonStyle(theme)}>Unlock</button>
        </form>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, marginBottom: 18 }}>
            <button onClick={() => setTab('requests')} style={getTabStyle(theme, tab === 'requests')}>Requests</button>
            <button onClick={() => setTab('ip-names')} style={getTabStyle(theme, tab === 'ip-names')}>IP Names</button>
          </div>

          {tab === 'requests' ? (
            <>
              <div style={{ marginBottom: 18 }}>
                <button onClick={() => fetchRequests(adminPassword)} style={primaryButtonStyle(theme)}>Refresh</button>
                <label style={{ cursor: 'pointer', fontWeight: 500, marginLeft: 16 }}>
                  <input type="checkbox" checked={hideCompleted} onChange={() => setHideCompleted((v) => !v)} style={{ marginRight: 8 }} />
                  Hide Completed
                </label>
                {lastUpdated && <div style={{ color: theme.subtext, fontSize: '0.85rem', marginTop: 5 }}>Last updated: {lastUpdated}</div>}
              </div>

              {requests.length > 0 ? (
                <div style={{ background: theme.cardBg, border: `1px solid ${theme.borderColor}`, borderRadius: 10, boxShadow: theme.shadow, padding: 18, marginTop: 12, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15, background: theme.cardBg, color: theme.text }}>
                    <thead>
                      <tr style={{ background: theme.tableHeader }}>
                        <th style={getThStyle(theme)}>Type</th>
                        <th style={getThStyle(theme)}>Title (Year)</th>
                        <th style={getThStyle(theme)}>OMDb ID</th>
                        <th style={getThStyle(theme)}>User</th>
                        <th style={getThStyle(theme)}>Note</th>
                        <th style={getThStyle(theme)}>Admin Note</th>
                        <th style={getThStyle(theme)}>Status</th>
                        <th style={getThStyle(theme)}>Time</th>
                        <th style={getThStyle(theme)}>Plex Status</th>
                        <th style={getThStyle(theme)}>Latest Episode</th>
                        <th style={getThStyle(theme)}>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((request, idx) => (
                        <tr key={request.id} style={{ borderBottom: `1px solid ${theme.borderColor}`, background: idx % 2 === 0 ? theme.tableRowBg : theme.tableRowAltBg }}>
                          <td style={getTdStyle(theme)}>{request.selection.media_type === 'movie' ? 'Movie' : request.selection.media_type === 'series' ? 'Show' : 'Unknown'}</td>
                          <td style={getTdStyle(theme)}>
                            {request.selection.title}
                            {request.selection.year ? ` (${request.selection.year})` : ''}
                          </td>
                          <td style={getTdStyle(theme)}>{request.selection.id}</td>
                          <td style={getTdStyle(theme)}>{request.user}</td>
                          <td style={getTdStyle(theme)}>{request.note}</td>
                          <td style={getTdStyle(theme)}>
                            <AdminNoteCell theme={theme} request={request} onSave={saveAdminNote} />
                          </td>
                          <td style={getTdStyle(theme)}>
                            <select
                              value={request.status}
                              onChange={(e) => updateStatus(request.id, e.target.value)}
                              style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${theme.borderColor}`, fontSize: 15, background: theme.inputBg, color: theme.text }}
                            >
                              {STATUS_OPTIONS.map((status) => (
                                <option key={status} value={status}>{status}</option>
                              ))}
                            </select>
                          </td>
                          <td style={getTdStyle(theme)}>{new Date(request.timestamp).toLocaleString()}</td>
                          <td style={getTdStyle(theme)}>
                            {request.plexStatus?.inPlex ? (
                              <div style={{ fontSize: '13px' }}>
                                <div style={{ color: '#4caf50', fontWeight: 'bold' }}>In Plex</div>
                                {request.selection.media_type === 'series' && request.plexStatus.latestEpisode && (
                                  <small>Latest: {request.plexStatus.latestEpisode}</small>
                                )}
                              </div>
                            ) : (
                              <div style={{ fontSize: '13px', color: '#d32f2f' }}>Not in Plex</div>
                            )}
                          </td>
                          <td style={getTdStyle(theme)}>{request.latestAiredEpisode || 'N/A'}</td>
                          <td style={getTdStyle(theme)}>
                            <button
                              style={{ color: '#fff', background: theme.deleteBg, border: 'none', borderRadius: 4, padding: '4px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 14, transition: 'background 0.2s' }}
                              onMouseOver={(e) => (e.currentTarget.style.background = theme.deleteHoverBg)}
                              onMouseOut={(e) => (e.currentTarget.style.background = theme.deleteBg)}
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to delete "${request.selection.title}"?`)) {
                                  deleteRequest(request.id);
                                }
                              }}
                              title="Delete this request"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                !error && (
                  <div style={{ marginTop: 24, color: theme.subtext, textAlign: 'center', fontSize: 16 }}>No requests found.</div>
                )
              )}
            </>
          ) : (
            <>
              <div style={{ marginBottom: 18 }}>
                <button onClick={() => fetchIpNames(adminPassword)} style={primaryButtonStyle(theme)}>Refresh</button>
                {ipLastUpdated && <div style={{ color: theme.subtext, fontSize: '0.85rem', marginTop: 5 }}>Last updated: {ipLastUpdated}</div>}
              </div>

              <form
                onSubmit={addIpName}
                style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', background: theme.cardBg, border: `1px solid ${theme.borderColor}`, borderRadius: 10, boxShadow: theme.shadow, padding: 18 }}
              >
                <input
                  placeholder="IP address (e.g. 192.168.1.42)"
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  style={inputStyle(theme, { width: 260 })}
                />
                <input
                  placeholder="Name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  style={inputStyle(theme, { width: 240 })}
                />
                <button type="submit" disabled={ipBusy} style={{ ...primaryButtonStyle(theme), opacity: ipBusy ? 0.6 : 1, cursor: ipBusy ? 'default' : 'pointer' }}>
                  {ipBusy ? 'Saving…' : 'Add / Update'}
                </button>
                <span style={{ color: theme.subtext, fontSize: '0.85rem' }}>Adding an existing IP overwrites its name.</span>
              </form>

              {ipMessage && (
                <div style={{ color: theme.successText, background: theme.successBg, borderRadius: 6, padding: '10px 16px', marginTop: 16, fontWeight: 500 }}>
                  {ipMessage}
                </div>
              )}
              {ipError && (
                <div style={{ color: theme.errorText, background: theme.errorBg, borderRadius: 6, padding: '10px 16px', marginTop: 16, fontWeight: 500 }}>
                  {ipError}
                </div>
              )}

              {sortedIpNames.length > 0 ? (
                <div style={{ background: theme.cardBg, border: `1px solid ${theme.borderColor}`, borderRadius: 10, boxShadow: theme.shadow, padding: 18, marginTop: 18, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15, background: theme.cardBg, color: theme.text }}>
                    <thead>
                      <tr style={{ background: theme.tableHeader }}>
                        <th style={getThStyle(theme)}>IP</th>
                        <th style={getThStyle(theme)}>Name</th>
                        <th style={getThStyle(theme)}>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedIpNames.map((entry, idx) => (
                        <tr key={entry.ip} style={{ borderBottom: `1px solid ${theme.borderColor}`, background: idx % 2 === 0 ? theme.tableRowBg : theme.tableRowAltBg }}>
                          <td style={{ ...getTdStyle(theme), fontFamily: 'monospace' }}>{entry.ip}</td>
                          <td style={getTdStyle(theme)}>{entry.name}</td>
                          <td style={getTdStyle(theme)}>
                            <button
                              style={{ color: '#fff', background: theme.deleteBg, border: 'none', borderRadius: 4, padding: '4px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 14, transition: 'background 0.2s' }}
                              onMouseOver={(e) => (e.currentTarget.style.background = theme.deleteHoverBg)}
                              onMouseOut={(e) => (e.currentTarget.style.background = theme.deleteBg)}
                              disabled={ipBusy}
                              onClick={() => {
                                if (window.confirm(`Delete mapping "${entry.name}" (${entry.ip})?`)) {
                                  deleteIpName(entry.ip, entry.name);
                                }
                              }}
                              title="Delete this IP mapping"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                !error && (
                  <div style={{ marginTop: 24, color: theme.subtext, textAlign: 'center', fontSize: 16 }}>No IP mappings yet.</div>
                )
              )}
            </>
          )}
        </>
      )}
      {error && <div style={{ color: theme.errorText, background: theme.errorBg, borderRadius: 6, padding: '10px 16px', marginTop: 16, fontWeight: 500 }}>{error}</div>}
    </div>
  );
}

export default Admin;
